import { useMemo, useState } from "react";
import { useAuditLogs } from "../../../hooks/useAuditLogs";
import type { AuditLogRecord } from "../../../lib/auditLogStore";

function formatDateTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value || "—";
  return date.toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function normalize(value?: string) {
  return String(value ?? "").toLocaleLowerCase("tr-TR").trim();
}

function matches(value: string | undefined, query: string) {
  const q = normalize(query);
  if (!q) return true;
  return normalize(value).includes(q);
}

function getChangeSummary(log: AuditLogRecord) {
  const changes = log.changes;
  if (!changes) return "—";

  const keys = Object.keys(changes).slice(0, 6);
  if (keys.length === 0) return "—";

  return keys
    .map(
      (key) =>
        `${key}: ${String(changes[key]?.before ?? "—")} → ${String(
          changes[key]?.after ?? "—"
        )}`
    )
    .join(" | ");
}

export default function LoglarPage() {
  const { logs, refreshLogs } = useAuditLogs();
  const [search, setSearch] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [pageFilter, setPageFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [recordFilter, setRecordFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");

  const filteredLogs = useMemo(() => {
    return logs
      .filter((log) => matches(formatDateTime(log.createdAt), dateFilter))
      .filter(
        (log) => matches(log.userName, userFilter) || matches(log.userRole, userFilter)
      )
      .filter(
        (log) => matches(log.page, pageFilter) || matches(log.table, pageFilter)
      )
      .filter((log) => matches(log.action, actionFilter))
      .filter((log) => matches(log.recordKey, recordFilter))
      .filter((log) => {
        if (!search.trim()) return true;
        const haystack = [
          formatDateTime(log.createdAt),
          log.userName,
          log.userRole,
          log.page,
          log.table,
          log.action,
          log.recordKey,
          log.detail,
          getChangeSummary(log),
        ].join(" ");
        return matches(haystack, search);
      })
      .slice(0, 5000);
  }, [logs, search, userFilter, pageFilter, actionFilter, recordFilter, dateFilter]);

  return (
    <div className="min-h-full bg-farm-cream p-4 lg:p-6">
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <button
            type="button"
            onClick={() => window.history.back()}
            className="mb-3 inline-flex items-center gap-1 rounded-full border border-farm-border bg-white px-3 py-1.5 text-xs font-semibold text-farm-brown hover:bg-farm-warm"
          >
            <i className="ri-arrow-left-line" /> Geri
          </button>
          <h1 className="font-heading text-2xl font-bold text-farm-brown">
            LOG / İşlem Geçmişi
          </h1>
          <p className="mt-1 text-xs text-farm-muted">
            Ekleme, silme ve düzenleme işlemleri burada Excel satırı gibi listelenir. Son 5000 kayıt tutulur.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void refreshLogs()}
          className="inline-flex h-10 items-center justify-center gap-2 rounded-full bg-farm-earth px-4 text-xs font-semibold text-white hover:bg-farm-brown"
        >
          <i className="ri-refresh-line" /> Yenile
        </button>
      </div>

      <div className="mb-3 grid gap-2 rounded-2xl border border-farm-border bg-white p-3 shadow-sm md:grid-cols-2 xl:grid-cols-6">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Genel arama" className="h-10 rounded-lg border border-farm-border px-3 text-xs outline-none focus:border-farm-earth" />
        <input value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} placeholder="Tarih/saat süz" className="h-10 rounded-lg border border-farm-border px-3 text-xs outline-none focus:border-farm-earth" />
        <input value={userFilter} onChange={(e) => setUserFilter(e.target.value)} placeholder="Kullanıcı süz" className="h-10 rounded-lg border border-farm-border px-3 text-xs outline-none focus:border-farm-earth" />
        <input value={pageFilter} onChange={(e) => setPageFilter(e.target.value)} placeholder="Sayfa / tablo süz" className="h-10 rounded-lg border border-farm-border px-3 text-xs outline-none focus:border-farm-earth" />
        <input value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} placeholder="İşlem tipi süz" className="h-10 rounded-lg border border-farm-border px-3 text-xs outline-none focus:border-farm-earth" />
        <input value={recordFilter} onChange={(e) => setRecordFilter(e.target.value)} placeholder="Fiş / küpe / kayıt süz" className="h-10 rounded-lg border border-farm-border px-3 text-xs outline-none focus:border-farm-earth" />
      </div>

      <div className="overflow-hidden rounded-2xl border border-farm-border bg-white shadow-sm">
        <div className="border-b border-farm-border bg-farm-warm px-4 py-2 text-xs font-semibold text-farm-brown">
          {filteredLogs.length.toLocaleString("tr-TR")} kayıt gösteriliyor / toplam {logs.length.toLocaleString("tr-TR")}
        </div>
        <div className="max-h-[calc(100vh-260px)] overflow-auto">
          <table className="min-w-[1300px] w-full border-collapse text-left text-xs">
            <thead className="sticky top-0 z-10 bg-farm-earth text-white">
              <tr>
                <th className="w-[155px] px-3 py-2 font-semibold">Tarih Saat</th>
                <th className="w-[150px] px-3 py-2 font-semibold">Kim</th>
                <th className="w-[130px] px-3 py-2 font-semibold">Sayfa</th>
                <th className="w-[130px] px-3 py-2 font-semibold">Tablo</th>
                <th className="w-[140px] px-3 py-2 font-semibold">Ne Yapıldı</th>
                <th className="w-[170px] px-3 py-2 font-semibold">Kayıt</th>
                <th className="px-3 py-2 font-semibold">Detay</th>
                <th className="px-3 py-2 font-semibold">Değişen Alanlar</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-farm-border/70">
              {filteredLogs.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-farm-muted">
                    Henüz log kaydı yok ya da filtreye uyan kayıt bulunamadı.
                  </td>
                </tr>
              ) : (
                filteredLogs.map((log) => (
                  <tr key={log.id} className="odd:bg-white even:bg-farm-cream/40 hover:bg-farm-warm/60">
                    <td className="whitespace-nowrap px-3 py-2 font-mono text-[11px] text-farm-muted">{formatDateTime(log.createdAt)}</td>
                    <td className="px-3 py-2 font-semibold text-farm-brown">
                      <div>{log.userName}</div>
                      {log.userRole && <div className="text-[10px] font-normal text-farm-muted">{log.userRole}</div>}
                    </td>
                    <td className="px-3 py-2 text-farm-brown">{log.page}</td>
                    <td className="px-3 py-2 text-farm-muted">{log.table}</td>
                    <td className="px-3 py-2">
                      <span className="rounded-full bg-farm-earth-pale px-2 py-1 text-[10px] font-bold text-farm-earth">{log.action}</span>
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px] text-farm-brown">{log.recordKey}</td>
                    <td className="px-3 py-2 text-farm-muted">{log.detail}</td>
                    <td className="px-3 py-2 text-[11px] text-farm-muted">{getChangeSummary(log)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
